// Searchable supplier combobox — filters active suppliers by name, with inline "Create new" option.

import { useState, useRef, useEffect } from "react";
import { ChevronDown, Plus } from "lucide-react";
import { useSuppliers } from "../../hooks/useSuppliers";
import type { Supplier } from "../../types";

interface SupplierComboboxProps {
  value?: string;
  onChange: (supplierId: string, supplierName: string) => void;
  onCreateNew?: () => void;
}

export default function SupplierCombobox({
  value,
  onChange,
  onCreateNew,
}: SupplierComboboxProps) {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data } = useSuppliers();
  const suppliers = data?.items?.filter((s) => s.status === "active") ?? [];

  const filtered = suppliers.filter(
    (s) => !query || s.name.toLowerCase().includes(query.toLowerCase()),
  );

  const selectedSupplier = suppliers.find((s) => s.id === value);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (supplier: Supplier) => {
    onChange(supplier.id, supplier.name);
    setQuery("");
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={isOpen ? query : selectedSupplier?.name || ""}
          onChange={(e) => {
            setQuery(e.target.value);
            if (!isOpen) setIsOpen(true);
          }}
          onFocus={() => {
            setIsOpen(true);
            setQuery("");
          }}
          placeholder="Select supplier..."
          className="input pr-8"
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-label="Supplier"
        />
        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
      </div>

      {isOpen && (
        <ul
          role="listbox"
          className="absolute z-10 mt-1 w-full max-h-60 overflow-auto rounded-lg bg-white border border-slate-200 shadow-soft-lg dark:shadow-soft-lg-dark dark:bg-slate-800 dark:border-slate-700"
        >
          {filtered.map((supplier) => (
            <li
              key={supplier.id}
              role="option"
              aria-selected={supplier.id === value}
              onClick={() => handleSelect(supplier)}
              className="px-3 py-2 cursor-pointer text-body-sm text-slate-900 dark:text-slate-50 hover:bg-brand-50 dark:hover:bg-brand-900/30"
            >
              {supplier.name}
            </li>
          ))}
          {filtered.length === 0 && (
            <li className="px-3 py-2 text-body-sm text-slate-500 dark:text-slate-400">
              No matching suppliers
            </li>
          )}
          {onCreateNew && (
            <li
              role="option"
              aria-selected={false}
              onClick={() => {
                setIsOpen(false);
                onCreateNew();
              }}
              className="px-3 py-2 cursor-pointer text-body-sm text-brand-600 dark:text-brand-400 hover:bg-brand-50 dark:hover:bg-brand-900/30 border-t border-slate-200 dark:border-slate-700 flex items-center gap-1"
            >
              <Plus className="h-3.5 w-3.5" />
              Create new supplier
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
